/**
 * Keyboard Shortcuts Plugin for Lexical
 * Handles common keyboard shortcuts for formatting and navigation
 *
 * Shortcuts:
 * - Cmd/Ctrl + S: Save
 * - Cmd/Ctrl + B: Bold
 * - Cmd/Ctrl + I: Italic
 * - Cmd/Ctrl + U: Underline
 * - Cmd/Ctrl + K: Insert link
 * - Cmd/Ctrl + Z: Undo
 * - Cmd/Ctrl + Shift + Z / Cmd/Ctrl + Y: Redo
 * - Cmd/Ctrl + Alt + 0: Normal text (paragraph)
 * - Cmd/Ctrl + Alt + 1: Title (h1)
 * - Cmd/Ctrl + Alt + 2: Subtitle (h2)
 * - Cmd/Ctrl + Alt + 3: Heading 1 (h3)
 * - Cmd/Ctrl + Alt + 4: Heading 2 (h4)
 * - Cmd/Ctrl + Alt + 5: Heading 3 (h5)
 * - Cmd/Ctrl + Shift + 7: Numbered list
 * - Cmd/Ctrl + Shift + 8: Bullet list
 * - Cmd/Ctrl + A: Select all (explicit handler needed - Lexical doesn't have a default)
 * - Cmd/Ctrl + Alt + T: Tag selection
 * - Cmd/Ctrl + Alt + E: Mark encryption
 * - Cmd/Ctrl + Shift + Arrow: Select to line boundary (explicit handler for RTL compatibility)
 */

import {
  $isListNode,
  INSERT_ORDERED_LIST_COMMAND,
  INSERT_UNORDERED_LIST_COMMAND,
  REMOVE_LIST_COMMAND,
} from '@lexical/list'
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext'
import { $createHeadingNode, HeadingTagType } from '@lexical/rich-text'
import { $setBlocksType } from '@lexical/selection'
import {
  $createParagraphNode,
  $getNearestNodeFromDOMNode,
  $getRoot,
  $getSelection,
  $isRangeSelection,
  $isTextNode,
  COMMAND_PRIORITY_LOW,
  FORMAT_TEXT_COMMAND,
  KEY_DOWN_COMMAND,
  REDO_COMMAND,
  SELECT_ALL_COMMAND,
  UNDO_COMMAND,
} from 'lexical'
import { useEffect } from 'react'

import { MARK_ENCRYPTION_COMMAND, OPEN_LINK_DIALOG_COMMAND, TAG_SELECTION_COMMAND } from '../LexicalToolbar'
import { $selectionContainsEmbed } from '../nodes'

export interface KeyboardShortcutsPluginProps {
  onSave?: () => void
}

export function KeyboardShortcutsPlugin({ onSave }: KeyboardShortcutsPluginProps) {
  const [editor] = useLexicalComposerContext()

  useEffect(() => {
    // Handle SELECT_ALL_COMMAND - Lexical doesn't have a default handler
    const unregisterSelectAll = editor.registerCommand(
      SELECT_ALL_COMMAND,
      () => {
        editor.update(() => {
          const root = $getRoot()
          root.select(0, root.getChildrenSize())
        })
        return true
      },
      COMMAND_PRIORITY_LOW,
    )

    // Use Lexical's command system for keyboard shortcuts
    const unregisterKeyDown = editor.registerCommand(
      KEY_DOWN_COMMAND,
      (event: KeyboardEvent) => {
        const isModifier = event.ctrlKey || event.metaKey
        if (!isModifier) {
          return false
        }

        // Handle Cmd/Ctrl + Shift + Arrow for visual line boundary selection (RTL fix)
        // Browsers have broken Cmd+Shift+Right in RTL contenteditable — it does nothing.
        // We find the visual line boundary using caretRangeFromPoint (asking "what character
        // is at the edge of this line?") and set Lexical's internal selection to that point.
        // The handler runs inside editor.update(), so $ functions work and the DOM is current.
        if (event.shiftKey && !event.altKey && (event.key === 'ArrowRight' || event.key === 'ArrowLeft')) {
          const sel = window.getSelection()
          if (sel && sel.focusNode) {
            // Get the Y position of the current visual line from the focus caret
            const focusRange = document.createRange()
            focusRange.setStart(sel.focusNode, sel.focusOffset)
            focusRange.collapse(true)
            const focusRect = focusRange.getBoundingClientRect()

            if (focusRect.height > 0) {
              // Find the block DOM element (has data-block-id) to get horizontal bounds
              let blockEl: HTMLElement | null = (
                sel.focusNode.nodeType === Node.TEXT_NODE ? sel.focusNode.parentElement : sel.focusNode
              ) as HTMLElement
              while (blockEl && !blockEl.getAttribute('data-block-id')) {
                blockEl = blockEl.parentElement
              }

              if (blockEl) {
                const blockRect = blockEl.getBoundingClientRect()
                // Target: the physical edge of the line in the arrow direction
                const targetX = event.key === 'ArrowRight' ? blockRect.right - 1 : blockRect.left + 1
                const targetY = focusRect.top + focusRect.height / 2

                const targetRange = document.caretRangeFromPoint(targetX, targetY)
                if (targetRange) {
                  // Convert DOM position to Lexical node + offset
                  const lexicalNode = $getNearestNodeFromDOMNode(targetRange.startContainer)
                  if (lexicalNode) {
                    const selection = $getSelection()
                    if ($isRangeSelection(selection)) {
                      if ($isTextNode(lexicalNode)) {
                        selection.focus.set(lexicalNode.getKey(), targetRange.startOffset, 'text')
                      } else {
                        selection.focus.set(lexicalNode.getKey(), targetRange.startOffset, 'element')
                      }
                      event.preventDefault()
                      return true
                    }
                  }
                }
              }
            }
          }
        }

        // Handle Cmd/Ctrl + Alt + 0-5 for text styles
        if (event.altKey && ['Digit0', 'Digit1', 'Digit2', 'Digit3', 'Digit4', 'Digit5'].includes(event.code)) {
          event.preventDefault()

          editor.update(() => {
            const selection = $getSelection()
            if (!$isRangeSelection(selection) || $selectionContainsEmbed(selection)) {
              return
            }

            switch (event.code) {
              case 'Digit0':
                // Normal text (paragraph)
                $setBlocksType(selection, () => $createParagraphNode())
                break
              case 'Digit1':
                // Title (h1)
                $setBlocksType(selection, () => $createHeadingNode('h1' as HeadingTagType))
                break
              case 'Digit2':
                // Subtitle (h2)
                $setBlocksType(selection, () => $createHeadingNode('h2' as HeadingTagType))
                break
              case 'Digit3':
                // Heading 1 (h3)
                $setBlocksType(selection, () => $createHeadingNode('h3' as HeadingTagType))
                break
              case 'Digit4':
                // Heading 2 (h4)
                $setBlocksType(selection, () => $createHeadingNode('h4' as HeadingTagType))
                break
              case 'Digit5':
                // Heading 3 (h5)
                $setBlocksType(selection, () => $createHeadingNode('h5' as HeadingTagType))
                break
            }
          })
          return true
        }

        // On macOS, Option-modified letters can report a dead-key or special character.
        // Match the physical key so Cmd+Option+E/T works reliably.
        if (event.altKey && event.code === 'KeyE') {
          event.preventDefault()
          editor.dispatchCommand(MARK_ENCRYPTION_COMMAND, undefined)
          return true
        }

        // Handle Cmd/Ctrl + Alt + T for tagging selection
        if (event.altKey && event.code === 'KeyT') {
          event.preventDefault()
          editor.dispatchCommand(TAG_SELECTION_COMMAND, undefined)
          return true
        }

        // Handle Cmd/Ctrl + Shift + 7/8 for lists (toggle: insert if not in list, remove if already in matching list)
        if (event.shiftKey && (event.code === 'Digit7' || event.code === 'Digit8')) {
          event.preventDefault()

          const targetType: 'number' | 'bullet' = event.code === 'Digit7' ? 'number' : 'bullet'
          let currentListType: 'number' | 'bullet' | null = null
          editor.getEditorState().read(() => {
            const selection = $getSelection()
            if (!$isRangeSelection(selection)) {
              return
            }
            const anchorNode = selection.anchor.getNode()
            const element = anchorNode.getKey() === 'root' ? anchorNode : anchorNode.getTopLevelElementOrThrow()
            if ($isListNode(element)) {
              currentListType = element.getListType() === 'number' ? 'number' : 'bullet'
            }
          })

          if (currentListType === targetType) {
            editor.dispatchCommand(REMOVE_LIST_COMMAND, undefined)
          } else if (targetType === 'number') {
            editor.dispatchCommand(INSERT_ORDERED_LIST_COMMAND, undefined)
          } else {
            editor.dispatchCommand(INSERT_UNORDERED_LIST_COMMAND, undefined)
          }
          return true
        }

        // Alt-modified combos were all handled above. Anything still holding Alt belongs
        // to the browser or the OS — ⌘⌥I opens devtools, ⌘⌥← switches tabs — and claiming
        // it here would both swallow that and apply the wrong formatting (⌘⌥I italic).
        if (event.altKey) {
          return false
        }

        // Match the physical key (event.code), not the produced character: with a
        // Hebrew layout active, Cmd+<physical S> reports key 'ד', so key-based
        // matching would silently break every letter shortcut.
        switch (event.code) {
          case 'KeyS':
            event.preventDefault()
            onSave?.()
            return true
          case 'KeyK':
            event.preventDefault()
            editor.dispatchCommand(OPEN_LINK_DIALOG_COMMAND, undefined)
            return true
          case 'KeyB':
            event.preventDefault()
            editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'bold')
            return true
          case 'KeyI':
            event.preventDefault()
            editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'italic')
            return true
          case 'KeyU':
            event.preventDefault()
            editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'underline')
            return true
          case 'KeyZ':
            event.preventDefault()
            if (event.shiftKey) {
              editor.dispatchCommand(REDO_COMMAND, undefined)
            } else {
              editor.dispatchCommand(UNDO_COMMAND, undefined)
            }
            return true
          case 'KeyY':
            event.preventDefault()
            editor.dispatchCommand(REDO_COMMAND, undefined)
            return true
          case 'KeyA':
            // Lexical's own select-all translation is key-based ('a') and misses
            // non-Latin layouts — dispatch it by physical key instead.
            event.preventDefault()
            editor.dispatchCommand(SELECT_ALL_COMMAND, event)
            return true
        }
        return false
      },
      COMMAND_PRIORITY_LOW,
    )

    return () => {
      unregisterSelectAll()
      unregisterKeyDown()
    }
  }, [onSave, editor])

  return null
}

export default KeyboardShortcutsPlugin
